// src/pages/PackPreviewPage.jsx
//
// PUBLIC pack free-preview (pack-free-preview slice) -- /preview/:packId, NOT
// behind AuthGuard. Lets a signed-out prospect read a pack's FREE items before
// creating an account (the funnel "feel the content" surface).
//
// Data comes from the public getPackPreview Cloud Function (free items ONLY --
// the CF can never return paid content; see functions/src/preview/). The page
// is strictly READ-ONLY: no AI Study Partner, no practice/grading, no notes/
// bookmarks/progress, no auth-writing action of any kind -- just the content
// plus a persistent "sign up to unlock" soft wall.
//
// The CONTENT renders through the SAME pure section renderers the real readers
// use (SetTextPreview / TechniqueSection / AnalysisSection / Lit*Section), so a
// free section looks identical to the real reader; the real readers themselves
// are untouched. AnnotatedText's context is absent here -> it pass-throughs
// (glossary popovers are a signed-in reader feature). All styling uses book-*
// tokens, so Day/Minimalist theme overrides apply automatically.
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPackPreview } from '../lib/callables.js';
import { AUTH_MODE } from '../auth/firebase.js';
import { useAuth } from '../auth/useAuth.js';
import { useSeoMeta } from '../components/usePageSeo.js';
import { previewSeo, PREVIEW_OFFER, PREVIEW_PURCHASE } from '../config/previewSeo.js';
import { IconSparkles } from '../components/icons.jsx';
import SetTextPreview from '../admin/SetTextPreview.jsx';
import TechniqueSection from './TechniqueSection.jsx';
import AnalysisSection from './AnalysisSection.jsx';
import VocabularySection from './VocabularySection.jsx';
import LitOverviewSection from './LitOverviewSection.jsx';
import LitActSection from './LitActSection.jsx';
import LitCharacterSection from './LitCharacterSection.jsx';
import LitThemeSection from './LitThemeSection.jsx';
import LitSymbolSection from './LitSymbolSection.jsx';
import LitQuotesSection from './LitQuotesSection.jsx';
import LitEssaySection from './LitEssaySection.jsx';
import LitMatrixSection from './LitMatrixSection.jsx';
import LitPoemSection from './LitPoemSection.jsx';
import { POETRY_OVERVIEW_LABELS } from '../config/poetryLabels.js';

// Item types the preview RENDERS as reading content. Everything else free
// (flashcards / quizzes / audio / essay prompts) is interactive practice --
// hidden in the read-only preview and surfaced as a "sign up to use" count.
const CONTENT_TYPES = ['lit_section', 'set_text'];

const PRACTICE_LABELS = {
    flashcard: 'flash cards',
    quiz: 'quiz questions',
    fill_in: 'quiz questions',
    matching: 'quiz questions',
    audio: 'audio lessons',
    essay_prompt: 'practice essay questions',
};

// Render one content item via the real reader renderers (read-only, pure).
function renderPreviewItem(item, packType) {
    if (item.type === 'set_text') return <SetTextPreview key={item.id} content={item.content} />;
    const kind = item.content && item.content.sectionKind;
    if (kind === 'technique') return <TechniqueSection key={item.id} content={item.content} />;
    if (kind === 'analysis') return <AnalysisSection key={item.id} content={item.content} />;
    if (kind === 'vocabulary') return <VocabularySection key={item.id} content={item.content} />;
    if (kind === 'poem') return <LitPoemSection key={item.id} content={item.content} />;
    if (kind === 'overview') {
        return packType === 'poetry-anthology'
            ? <LitOverviewSection key={item.id} content={item.content} labels={POETRY_OVERVIEW_LABELS} />
            : <LitOverviewSection key={item.id} content={item.content} />;
    }
    if (kind === 'act') return <LitActSection key={item.id} content={item.content} />;
    if (kind === 'character') return <LitCharacterSection key={item.id} content={item.content} />;
    if (kind === 'theme') return <LitThemeSection key={item.id} content={item.content} />;
    if (kind === 'symbol') return <LitSymbolSection key={item.id} content={item.content} />;
    if (kind === 'quotes') return <LitQuotesSection key={item.id} content={item.content} />;
    if (kind === 'essay') return <LitEssaySection key={item.id} content={item.content} />;
    if (kind === 'matrix') {
        return packType === 'poetry-anthology'
            ? <LitMatrixSection key={item.id} content={item.content} titleFallback="Poem × Theme Comparison" groupTag="Comparison" />
            : <LitMatrixSection key={item.id} content={item.content} />;
    }
    return (
        <div key={item.id} className="border border-book-accent/20 bg-book-page/30 p-10 text-center">
            <p className="font-serif italic text-book-muted">This section is available in the full pack.</p>
        </div>
    );
}

function itemLabel(item) {
    return (item.content && item.content.title) || item.title || 'Section';
}

// "1 flash cards" -> "1 flash card" (labels are plural; trim the s when n === 1).
function countLabel(n, label) {
    return n + ' ' + (n === 1 ? label.replace(/s$/, '') : label);
}

export default function PackPreviewPage() {
    const { packId } = useParams();
    const navigate = useNavigate();
    const { currentUser: user } = useAuth();

    const [state, setState] = useState('loading'); // loading | ok | not_available | error
    const [data, setData] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    useSeoMeta(previewSeo(packId, data?.pack));

    useEffect(() => {
        let cancelled = false;
        setState('loading'); setData(null); setSelectedId(null);
        if (AUTH_MODE === 'mock') { setState('not_available'); return undefined; }
        getPackPreview({ packId })
            .then((res) => {
                if (cancelled) return;
                if (res && res.status === 'ok') { setData(res); setState('ok'); }
                else setState('not_available');
            })
            .catch((e) => {
                if (cancelled) return;
                console.error('PackPreviewPage:', e);
                setState('error');
            });
        return () => { cancelled = true; };
    }, [packId]);

    const contentItems = useMemo(() => (data ? data.items.filter((i) => CONTENT_TYPES.includes(i.type)) : []), [data]);
    const practiceCounts = useMemo(() => {
        const counts = new Map();
        for (const it of (data ? data.items : [])) {
            if (CONTENT_TYPES.includes(it.type)) continue;
            const label = PRACTICE_LABELS[it.type];
            if (label) counts.set(label, (counts.get(label) || 0) + 1);
        }
        return [...counts.entries()];
    }, [data]);

    const active = contentItems.find((i) => i.id === selectedId) || contentItems[0] || null;
    const signup = () => navigate(user ? '/dashboard' : '/login?mode=signup');
    const cta = user ? 'Enter Study Packs →' : 'Create a free account →';

    return (
        <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-book-bg paper-texture">
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
                {/* Preview banner + persistent soft wall */}
                <div className="mb-6 border border-book-accent/30 bg-book-page/40 p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                    <div>
                        <div className="font-display text-book-accent text-xs mb-1 font-semibold">Free sample</div>
                        <p className="font-serif italic text-book-muted text-sm">
                            You're previewing the free sample{data ? <> of <span className="text-book-text not-italic">{data.pack.title}</span></> : null}.
                        </p>
                    </div>
                    <button onClick={signup}
                        className="px-5 py-2.5 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors btn-sweep flex-shrink-0 font-semibold">
                        {cta}
                    </button>
                </div>

                {data && <div className="mb-8 space-y-2"><h1 className="font-display text-2xl font-bold">{data.pack.title} — Free lesson</h1><p className="text-sm text-book-muted leading-relaxed">{PREVIEW_OFFER}</p><p className="text-xs text-book-muted">{PREVIEW_PURCHASE}</p></div>}

                {state === 'loading' && (
                    <p className="font-serif italic text-book-muted text-center py-16">Loading the free sample…</p>
                )}

                {state === 'not_available' && (
                    <div className="border border-book-accent/20 bg-book-page/30 p-10 text-center space-y-4">
                        <p className="font-serif italic text-book-muted">This pack isn't available to preview.</p>
                        <button onClick={() => navigate('/home')}
                            className="px-5 py-2.5 border border-book-accent/50 text-book-accent font-display text-xs hover:bg-book-accent/10 transition-colors font-semibold">
                            Browse the study packs
                        </button>
                    </div>
                )}

                {state === 'error' && (
                    <div className="border border-amber-500/40 bg-amber-500/5 p-10 text-center">
                        <p className="font-serif text-book-text text-sm">
                            The preview couldn't load just now. Please try again in a moment.
                        </p>
                    </div>
                )}

                {state === 'ok' && contentItems.length === 0 && (
                    <div className="border border-book-accent/20 bg-book-page/30 p-10 text-center space-y-3">
                        <p className="font-serif italic text-book-muted">
                            This pack's free sample lives in its practice tools
                            {practiceCounts.length > 0 ? ' (' + practiceCounts.map(([l, n]) => countLabel(n, l)).join(' · ') + ')' : ''}.
                        </p>
                        <p className="font-serif text-book-text text-sm">{user ? 'Open the study packs to try the available free activities.' : 'Create a free account to try the available free activities.'}</p>
                    </div>
                )}

                {state === 'ok' && contentItems.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
                        {/* LEFT: free-section nav (read-only) */}
                        <aside className="border border-book-accent/20 bg-book-page/30 p-2 h-fit lg:sticky lg:top-4 max-h-[70vh] overflow-y-auto custom-scrollbar">
                            <div className="font-display text-book-accent text-xs px-3 pt-2 pb-1 font-semibold">
                                Free sections
                            </div>
                            {contentItems.map((it) => {
                                const isActive = active && it.id === active.id;
                                return (
                                    <button key={it.id} onClick={() => setSelectedId(it.id)}
                                        className={`w-full text-left px-3 py-2 font-serif text-sm transition-colors ${isActive
                                            ? 'bg-book-accent/15 text-book-text border-l-2 border-book-accent'
                                            : 'text-book-muted hover:text-book-text hover:bg-book-accent/5'}`}>
                                        {itemLabel(it)}
                                    </button>
                                );
                            })}
                            {/* Locked remainder cue */}
                            <div className="mt-2 mx-2 border-t border-book-accent/15 pt-3 pb-2 px-1">
                                <p className="font-serif italic text-book-muted text-xs leading-relaxed mb-2">
                                    The full pack adds the complete set of sections
                                    {practiceCounts.length > 0 ? ' plus ' + practiceCounts.map(([l, n]) => n + ' free ' + (n === 1 ? l.replace(/s$/, '') : l)).join(' · ') + ' and more' : ''}.
                                </p>
                                <button onClick={signup}
                                    className="w-full py-2 border border-book-accent/50 text-book-accent font-display text-xs hover:bg-book-accent/10 transition-colors inline-flex items-center justify-center gap-1.5 font-semibold">
                                    <IconSparkles className="w-3.5 h-3.5" /> {cta}
                                </button>
                            </div>
                        </aside>

                        {/* RIGHT: the selected free section, rendered by the real reader renderers */}
                        <div className="min-w-0">
                            {active && renderPreviewItem(active, data.pack.packType)}

                            {/* Persistent soft-wall CTA under the content */}
                            <div className="mt-6 border border-book-accent/30 bg-book-page/40 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                                <div>
                                    <p className="font-serif text-book-text leading-snug">Like what you're reading?</p>
                                    <p className="font-serif italic text-book-muted text-sm">
                                        {PREVIEW_PURCHASE}
                                    </p>
                                </div>
                                <button onClick={signup}
                                    className="px-5 py-3 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors btn-sweep flex-shrink-0 font-semibold">
                                    {cta}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
